/* eslint-disable @typescript-eslint/naming-convention -- campos em snake_case espelham o contrato da API */
import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subject,
  Subscription,
  throwError,
  timer
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  takeUntil
} from 'rxjs/operators';
import { ChatApi } from '../apis/chat.api';
import { EnumConversationState } from '../enums/conversation-state.enum';
import { IConversationItem } from '../interfaces/conversation-history.interface';
import { IConversationView } from '../interfaces/conversation-view.interface';

export const MAX_PINNED = 5;
export const POLL_INTERVAL_MS = 5_000;

const parouSemResposta = (state?: EnumConversationState): boolean =>
  state === EnumConversationState.Error ||
  state === EnumConversationState.WaitingHitl;

@Injectable({ providedIn: 'root' })
export class ChatHistoryStateService implements OnDestroy {
  private readonly CACHE_TTL_MS = 30_000;

  private items$ = new BehaviorSubject<IConversationItem[]>([]);
  private visibleSessions$ = new BehaviorSubject<ReadonlyMap<string, string>>(
    new Map()
  );
  private reset$ = new Subject<void>();
  readonly resetObservable = this.reset$.asObservable();
  private activeSessionId$ = new BehaviorSubject<string | null>(null);
  private loading$ = new BehaviorSubject<boolean>(false);
  private lastLoadedAt: number | null = null;

  private pendingPinToggles = new Set<string>();

  private generationCompleted$ = new Subject<IConversationItem>();
  private awaitingApproval$ = new Subject<IConversationItem>();
  /** Cursor do mesmo snapshot da lista: o canal assina a partir dele. */
  private snapshotCursor: string | null = null;
  private snapshotLoaded$ = new Subject<string | null>();
  /** Conversas excluídas e a revisão da exclusão: impede que evento atrasado
   * ou replay ressuscite o item. */
  private readonly removed = new Map<string, number>();
  private pollingSub: Subscription | null = null;
  private isFetching = false;

  readonly itemsObservable: Observable<IConversationItem[]> =
    this.items$.asObservable();
  readonly activeIdObservable: Observable<string | null> =
    this.activeSessionId$.asObservable();
  readonly loadingObservable: Observable<boolean> =
    this.loading$.asObservable();

  readonly pinnedCount$: Observable<number> = this.items$.pipe(
    map((items) => items.filter((i) => i.pinned).length),
    distinctUntilChanged()
  );

  readonly canPinMore$: Observable<boolean> = this.pinnedCount$.pipe(
    map((count) => count < MAX_PINNED),
    distinctUntilChanged()
  );

  readonly generationCompletedObservable: Observable<IConversationItem> =
    this.generationCompleted$.asObservable();

  readonly awaitingApprovalObservable: Observable<IConversationItem> =
    this.awaitingApproval$.asObservable();

  /** Emite o cursor de cada snapshot: o canal (re)assina a partir dele. */
  readonly snapshotLoadedObservable: Observable<string | null> =
    this.snapshotLoaded$.asObservable();

  get activeSessionId(): string | null {
    return this.activeSessionId$.getValue();
  }

  get pinnedCount(): number {
    return this.items$.getValue().filter((i) => i.pinned).length;
  }

  constructor(private chatApi: ChatApi) {
    document.addEventListener('visibilitychange', this.onFocusRefetch);
    window.addEventListener('focus', this.onFocusRefetch);
  }

  /** Rede de segurança: o canal do usuário é a fonte, mas SSE morre em proxy,
   * rede instável e aba suspensa. Sem isso, a lista congelaria na queda. */
  setFallbackPolling(ativo: boolean): void {
    if (ativo) {
      this.startPolling();
      return;
    }
    this.stopPolling();
  }

  ngOnDestroy(): void {
    this.reset$.next();
    this.reset$.complete();
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.onFocusRefetch);
    window.removeEventListener('focus', this.onFocusRefetch);
  }

  load(force = false): void {
    if (!force && this.isFresh()) {
      return;
    }
    this.fetchSessions();
  }

  addPlaceholder(sessionId: string): void {
    const placeholder: IConversationItem = {
      id: sessionId,
      title: null,
      date: new Date(),
      pinned: false,
      isGenerating: false,
      hasUnseenResult: false
    };
    const current = this.items$.getValue();
    if (current.some((i) => i.id === sessionId)) {
      return;
    }
    this.items$.next(this.sortByPinThenDate([placeholder, ...current]));
  }

  /** A seleção da barra lateral corresponde apenas à página. */
  markActive(sessionId: string | null): void {
    this.setVisible('page', sessionId);
  }

  setVisible(consumer: string, sessionId: string | null): void {
    const next = new Map(this.visibleSessions$.value);
    if (sessionId) {
      next.set(consumer, sessionId);
      this.patchItem(sessionId, { hasUnseenResult: false });
    } else {
      next.delete(consumer);
    }
    this.visibleSessions$.next(next);
    if (consumer === 'page' && this.activeSessionId$.value !== sessionId) {
      this.activeSessionId$.next(sessionId);
    }
  }

  /** Marca só a geração: o `state` é versionado e vem pelos eventos, senão o
   * início de uma run sobrescreveria um `waiting_hitl` mais novo. */
  markGenerating(sessionId: string): void {
    this.patchItem(sessionId, { isGenerating: true });
  }

  /** O stream informa o estado uma vez; a lista o exibe até o próximo evento. */
  setState(
    sessionId: string,
    state: EnumConversationState,
    revision?: number
  ): void {
    const item = this.items$.value.find(
      (candidate) => candidate.id === sessionId
    );
    if (!item || this.isStale(item.stateRevision, revision)) {
      return;
    }
    // Arquivada sai da listagem ativa, como o REST já faz.
    if (state === EnumConversationState.Archived) {
      this.remove(sessionId, revision);
      return;
    }
    // Revisão nova com o mesmo valor não muda a tela, mas precisa ser guardada
    // para que a próxima comparação não aceite um evento realmente antigo.
    const mudou = item.state !== state;

    if (state === EnumConversationState.Idle && item.isGenerating) {
      // Sair de `processing` é a conclusão da resposta: quem acende o aviso de
      // não vista é o `markIdle`, que compara com o que estava em curso.
      this.markIdle(sessionId, revision);
      return;
    }

    this.patchItem(sessionId, {
      state,
      ...(revision !== undefined && { stateRevision: revision }),
      ...(state === EnumConversationState.Processing && { isGenerating: true }),
      ...(parouSemResposta(state) && { isGenerating: false })
    });
    // Aguardar aprovação trava a conversa: quem saiu dela precisa ser chamado.
    if (
      mudou &&
      state === EnumConversationState.WaitingHitl &&
      !this.isVisible(sessionId)
    ) {
      this.awaitingApproval$.next({ ...item, state });
    }
  }

  markIdle(sessionId: string, revision?: number): void {
    const item = this.items$.value.find(
      (candidate) => candidate.id === sessionId
    );
    const unseen = !!item?.isGenerating && !this.isVisible(sessionId);
    this.patchItem(sessionId, {
      isGenerating: false,
      state: EnumConversationState.Idle,
      ...(revision !== undefined && { stateRevision: revision }),
      ...(unseen && { hasUnseenResult: true })
    });
    if (unseen) {
      this.generationCompleted$.next({
        ...item!,
        isGenerating: false,
        hasUnseenResult: true
      });
    }
  }

  /** Falha, pausa ou cancelamento não são resposta pronta: o estado da conversa
   * segue o evento versionado, que pode chegar antes ou depois deste término. */
  markRunStopped(sessionId: string): void {
    const item = this.items$.value.find(
      (candidate) => candidate.id === sessionId
    );
    this.patchItem(sessionId, {
      isGenerating: false,
      ...(item?.state === EnumConversationState.Processing && {
        state: EnumConversationState.Idle
      })
    });
  }

  remove(sessionId: string, revision?: number): void {
    this.removed.set(sessionId, revision ?? Number.MAX_SAFE_INTEGER);
    const current = this.items$.getValue();
    this.items$.next(current.filter((i) => i.id !== sessionId));
  }

  addOrUpdateItem(item: IConversationItem): void {
    const current = this.items$.getValue();
    const idx = current.findIndex((i) => i.id === item.id);

    // Replay e páginas antigas podem trazer de volta o que já foi excluído.
    if (this.isRemoved(item)) {
      return;
    }

    if (idx === -1) {
      this.items$.next(this.sortByPinThenDate([item, ...current]));
      return;
    }

    const existing = current[idx];
    const titleChanged = item.title !== null && existing.title !== item.title;
    const respectPending = this.pendingPinToggles.has(item.id);
    const pinChanged = !respectPending && existing.pinned !== item.pinned;
    const generatingChanged =
      item.isGenerating !== undefined &&
      (existing.isGenerating ?? false) !== item.isGenerating;

    if (!titleChanged && !pinChanged && !generatingChanged) {
      return;
    }

    const updated: IConversationItem = {
      ...existing,
      title: titleChanged ? item.title : existing.title,
      pinned: pinChanged ? item.pinned : existing.pinned,
      isGenerating: generatingChanged
        ? item.isGenerating
        : existing.isGenerating
    };
    const next = [...current.slice(0, idx), updated, ...current.slice(idx + 1)];
    this.items$.next(this.sortByPinThenDate(next));
  }

  moveToTop(sessionId: string, when: Date = new Date()): void {
    const current = this.items$.getValue();
    const idx = current.findIndex((i) => i.id === sessionId);
    if (idx === -1) {
      return;
    }
    const updated = { ...current[idx], date: when };
    const next = [...current.slice(0, idx), updated, ...current.slice(idx + 1)];
    this.items$.next(this.sortByPinThenDate(next));
  }

  setTitle(sessionId: string, title: string | null, revision?: number): void {
    const current = this.items$.getValue();
    const idx = current.findIndex((i) => i.id === sessionId);
    if (idx === -1 || this.isStale(current[idx].titleRevision, revision)) {
      return;
    }
    if (current[idx].title === title && revision === undefined) {
      return;
    }
    const updated = {
      ...current[idx],
      title,
      ...(revision !== undefined && { titleRevision: revision })
    };
    this.items$.next([
      ...current.slice(0, idx),
      updated,
      ...current.slice(idx + 1)
    ]);
  }

  rename(sessionId: string, title: string): Observable<void> {
    const idx = this.items$.getValue().findIndex((i) => i.id === sessionId);
    if (idx === -1) {
      return throwError(
        new Error(`Sessão ${sessionId} não está no histórico.`)
      );
    }
    const previous = this.items$.getValue()[idx].title;

    this.setTitle(sessionId, title);

    return this.chatApi.renameConversation(sessionId, title).pipe(
      map(() => undefined),
      catchError((err) => {
        const current = this.items$.getValue().find((i) => i.id === sessionId);
        if (current && current.title === title) {
          this.setTitle(sessionId, previous);
        }
        return throwError(err);
      })
    );
  }

  setPinned(sessionId: string, pinned: boolean): void {
    const current = this.items$.getValue();
    const idx = current.findIndex((i) => i.id === sessionId);
    if (idx === -1) {
      return;
    }
    if (current[idx].pinned === pinned) {
      return;
    }

    const updated = { ...current[idx], pinned };
    const next = [...current.slice(0, idx), updated, ...current.slice(idx + 1)];
    this.items$.next(this.sortByPinThenDate(next));
  }

  isPinPending(sessionId: string): boolean {
    return this.pendingPinToggles.has(sessionId);
  }

  markPinPending(sessionId: string): void {
    this.pendingPinToggles.add(sessionId);
  }

  // O singleton sobrevive ao logout; sem reset a próxima conta veria as conversas da anterior.
  reset(): void {
    this.reset$.next();
    this.visibleSessions$.next(new Map());
    this.stopPolling();
    this.isFetching = false;
    this.lastLoadedAt = null;
    this.pendingPinToggles.clear();
    this.items$.next([]);
    this.activeSessionId$.next(null);
    this.loading$.next(false);
  }

  clearPinPending(sessionId: string): void {
    this.pendingPinToggles.delete(sessionId);
  }

  private isVisible(sessionId: string): boolean {
    return [...this.visibleSessions$.value.values()].includes(sessionId);
  }

  /** O local vence onde a tela já sabe mais que a página: toggle otimista
   * pendente, conversa aberta e campo de revisão mais nova. */
  private mergeItem(
    local: IConversationItem,
    base: IConversationItem
  ): IConversationItem {
    return {
      ...base,
      ...this.keepNewerFields(local, base),
      // A listagem não ordena por data: adotar a dela recuaria a conversa que
      // acabou de responder, remontando a lista sob o cursor.
      date: local.date > base.date ? local.date : base.date,
      hasUnseenResult: local.hasUnseenResult ?? false,
      pinned: this.pendingPinToggles.has(base.id) ? local.pinned : base.pinned,
      isGenerating: this.isVisible(base.id)
        ? (local.isGenerating ?? false)
        : base.isGenerating
    };
  }

  /** Terminou enquanto o usuário olhava outra coisa: vira aviso. */
  private concluiuForaDaTela(
    local: IConversationItem,
    merged: IConversationItem
  ): boolean {
    return (
      !this.isVisible(merged.id) &&
      !!local.isGenerating &&
      !merged.isGenerating &&
      !parouSemResposta(merged.state)
    );
  }

  /** Uma página antiga não pode desfazer o campo que o canal já atualizou. */
  private keepNewerFields(
    local: IConversationItem,
    vindoDoServidor: IConversationItem
  ): Partial<IConversationItem> {
    return {
      ...(this.isStale(local.stateRevision, vindoDoServidor.stateRevision) && {
        state: local.state,
        stateRevision: local.stateRevision
      }),
      ...(this.isStale(local.titleRevision, vindoDoServidor.titleRevision) && {
        title: local.title,
        titleRevision: local.titleRevision
      })
    };
  }

  /** Sem revisão, o evento é aplicado; com ela, só se for mais nova. */
  private isStale(atual?: number, recebida?: number): boolean {
    return (
      atual !== undefined && recebida !== undefined && recebida <= atual
    );
  }

  private isRemoved(item: IConversationItem): boolean {
    const revisaoDaExclusao = this.removed.get(item.id);
    if (revisaoDaExclusao === undefined) {
      return false;
    }
    // A exclusão é versionada no mesmo espaço do estado; `titleRevision` conta
    // outra coisa e compará-la aqui reviveria (ou sepultaria) por acidente.
    return (
      item.stateRevision === undefined ||
      item.stateRevision <= revisaoDaExclusao
    );
  }

  private patchItem(
    sessionId: string,
    patch: Partial<IConversationItem>
  ): void {
    const current = this.items$.getValue();
    const idx = current.findIndex((i) => i.id === sessionId);
    if (idx === -1) {
      return;
    }
    const existing = current[idx];
    const keys = Object.keys(patch) as (keyof IConversationItem)[];
    if (keys.every((k) => (existing[k] ?? false) === (patch[k] ?? false))) {
      return;
    }
    const updated = { ...existing, ...patch };
    this.items$.next([
      ...current.slice(0, idx),
      updated,
      ...current.slice(idx + 1)
    ]);
  }

  private fetchSessions(): void {
    if (this.isFetching) {
      return;
    }
    this.isFetching = true;
    this.loading$.next(true);
    this.chatApi
      .listConversations()
      .pipe(takeUntil(this.reset$))
      .subscribe({
        next: (listing) => {
          const { merged, completed } = this.mergeServerListing(listing.items);
          this.items$.next(merged);
          this.snapshotCursor = listing.userStreamCursor;
          this.snapshotLoaded$.next(listing.userStreamCursor);
          this.lastLoadedAt = Date.now();
          this.loading$.next(false);
          this.isFetching = false;
          completed.forEach((i) => this.generationCompleted$.next(i));
        },
        error: () => {
          this.loading$.next(false);
          this.isFetching = false;
        }
      });
  }

  private mergeServerListing(server: IConversationView[]): {
    merged: IConversationItem[];
    completed: IConversationItem[];
  } {
    const localById = new Map(this.items$.getValue().map((i) => [i.id, i]));
    const completed: IConversationItem[] = [];

    const incoming = server
      .map(toConversationItem)
      // A página pode ser anterior à exclusão já conhecida aqui.
      .filter((base) => !this.isRemoved(base))
      .map((base) => {
        const local = localById.get(base.id);
        if (!local) {
          return base;
        }
        const merged = this.mergeItem(local, base);
        if (this.concluiuForaDaTela(local, merged)) {
          merged.hasUnseenResult = true;
          completed.push(merged);
        }
        return merged;
      });

    const incomingIds = new Set(incoming.map((i) => i.id));
    const optimistic = this.items$
      .getValue()
      .filter((i) => !incomingIds.has(i.id) && i.title === null);

    return {
      merged: this.sortByPinThenDate([...optimistic, ...incoming]),
      completed
    };
  }

  private startPolling(): void {
    if (this.pollingSub) {
      return;
    }
    this.pollingSub = timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS).subscribe(() =>
      this.fetchSessions()
    );
  }

  private stopPolling(): void {
    if (!this.pollingSub) {
      return;
    }
    this.pollingSub.unsubscribe();
    this.pollingSub = null;
  }

  private onFocusRefetch = (): void => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    const hasBackgroundGeneration = this.items$
      .getValue()
      .some((i) => i.isGenerating && !this.isVisible(i.id));
    if (!hasBackgroundGeneration) {
      return;
    }
    this.fetchSessions();
  };

  private sortByPinThenDate(items: IConversationItem[]): IConversationItem[] {
    return [...items].sort(
      (a, b) =>
        Number(b.pinned) - Number(a.pinned) ||
        b.date.getTime() - a.date.getTime()
    );
  }

  private isFresh(): boolean {
    if (this.lastLoadedAt === null) {
      return false;
    }
    return Date.now() - this.lastLoadedAt < this.CACHE_TTL_MS;
  }
}

function toConversationItem(s: IConversationView): IConversationItem {
  return {
    id: s.id,
    title: s.title,
    date: new Date(s.lastMessageAt ?? s.createdAt),
    pinned: s.isPinned ?? false,
    messageCount: s.messageCount,
    state: s.state,
    stateRevision: s.stateRevision,
    titleRevision: s.titleRevision,
    isGenerating: s.state === EnumConversationState.Processing
  };
}
